/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, Key, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Types, Heading, Accordion, Link, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { CARIBBEAN_LABEL, TRACKING_CONST } from '../../constants/RegionCard.constants';
import { StyledRegionCard } from './RegionCard.styles';
import { RegionCardProps } from './RegionCard.types';
import { RegionItem } from '../RegionItem';
import { formatNumberByLocale } from '../../utils/src/utils';
import { changeRegionNames, formatExitURL } from '../../organisms/RegionCardsWrapper/RegionCardsWrapperHelper';

export const RegionCard: FC<RegionCardProps> = ({
  region,
  name,
  code,
  count,
  customLabel,
  viewAllHotelsLabel,
  unitedStatesLabel,
  australiaPILabel,
  subRegionList,
  setSelectedRegion,
  selectedRegion,
  hotelsLabel,
  currentLocale,
  brandCode,
}) => {
  const [localStateOrCountryList, setLocalStateOrCountryList] = useState<React.ReactElement[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [openAccordion, setOpenAccordion] = useState<boolean>(true);
  const isViewportM = useCheckBreakpoint('viewportM');
  const { HOTEL_DIRECTORY, REGION_CARD } = TRACKING_CONST;

  /**
   * Updates the list of sub-regions (states or countries) to be displayed
   * when subRegionList changes.
   */
  useEffect(() => {
    if (subRegionList?.length && subRegionList[0]?.count > 0) {
      const isSingleRegion = subRegionList.length === 1 && subRegionList[0].label === name;

      if (!isSingleRegion) {
        const results = subRegionList.map((each: any, idx: Key) => (
          <RegionItem
            key={idx}
            name={each.label}
            count={each.count}
            code={each.code}
            regionCode={selectedRegion?.code}
            custom_click_track_value={`${HOTEL_DIRECTORY} | Region Card: ${selectedRegion?.label}: ${each.label} | internal`}
            brandCode={brandCode}
          />
        ));
        setLocalStateOrCountryList(results);
      } else {
        setLocalStateOrCountryList([]);
      }
    } else {
      setLocalStateOrCountryList([]);
    }

    setLoading(false);
  }, [subRegionList, name, selectedRegion]);

  /**
   * Handles click on the region card to update selected region.
   */
  const handleCardClick = () => {
    if (selectedRegion?.code !== code) {
      setLoading(true);
      setSelectedRegion({
        code,
        label: name,
        count,
        customLabel,
      });
    }
  };

  /**
   * Controls accordion open state based on selected region.
   */
  useEffect(() => {
    setOpenAccordion(code === selectedRegion?.code);
  }, [code, selectedRegion]);

  const formattedHeading = `${changeRegionNames(region, unitedStatesLabel, australiaPILabel)} (${formatNumberByLocale(
    count,
    currentLocale?.replace('_', '-')
  )} ${hotelsLabel})`;

  const viewAllLinkText =
    name === unitedStatesLabel || name === CARIBBEAN_LABEL
      ? `${viewAllHotelsLabel} ${name}`
      : `${viewAllHotelsLabel.replace('the', '')} ${name}`;

  const viewAllLinkHref = formatExitURL(code, name, selectedRegion?.code, brandCode, false, true);

  return (
    <StyledRegionCard
      data-component-name="mi-brands-regionCard"
      data-testid="brands-regioncard"
      className={clsx('container', 'px-0')}
      onClick={handleCardClick}
      role="region"
    >
      <div className={clsx('region-card-container', 'px-0')}>
        <div className={clsx('accordion-wrapper')}>
          <Accordion
            id={code}
            isOpen={openAccordion}
            custom_click_track_open_value={`${HOTEL_DIRECTORY} | ${REGION_CARD}: ${name} | internal`}
            custom_click_track_closed_value={`${HOTEL_DIRECTORY} | ${REGION_CARD}: ${name} | internal`}
            headerChildren={[
              <div key="header" className={clsx('d-flex', 'my-4', 'region-card-parent-container')}>
                <div
                  className={clsx('d-flex', 'flex-column', 'justify-content-center', 'align-items-start', 'text-left')}
                >
                  <Heading
                    variation={Types.headingType.subtitle}
                    titleText={formattedHeading}
                    fontSize={Types.size.medium}
                    element={Types.tags.h3}
                    customClass={clsx('mb-0', 'region-card-title')}
                  />
                </div>
              </div>,
            ]}
          >
            {isLoading
              ? null
              : count > 0 && (
                  <div className={clsx('region-items-container')}>
                    {localStateOrCountryList.length > 0 && (
                      <div className={clsx('row', isViewportM ? 'mt-4' : 'mt-2')}>{localStateOrCountryList}</div>
                    )}
                    <div className={clsx('my-4', 'pt-3', 'region-links-container')}>
                      <Link
                        text={viewAllLinkText}
                        linkHref={viewAllLinkHref}
                        linkClassName={clsx('t-subtitle-m', 'region-card-view-all-link', 'custom_click_track')}
                        ariaLabel={`All hotels in ${name}`}
                        target="_self"
                        custom_click_track_value={`${HOTEL_DIRECTORY} | View All Hotels in the ${name} | internal`}
                      />
                    </div>
                  </div>
                )}
          </Accordion>
        </div>
      </div>
    </StyledRegionCard>
  );
};
